import React from 'react'
import CardUI from '../../../widgets/layout/CardUI'
import { KvRowUI, KvSectionUI } from '../../../widgets/layout/KvRowUI'

const formatCashFlowLabel = (label) => {
  const spaced = label
    .replace(/([a-z])([A-Z0-9])/g, '$1 $2')
    .replace(/(\d+)/g, ' $1')
    .trim()
  return spaced.charAt(0).toUpperCase() + spaced.slice(1)
}

const formatPaise = (raw) => {
  const num = Number(String(raw).replace(/,/g, ''))
  if (Number.isNaN(num)) return raw
  return `₹${(num / 100).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
}

const formatMonthRange = (start, end) => {
  if (start === end) return `Month ${start}`
  return `Months ${start}–${end}`
}

const formatApr = (value) => {
  if (value == null || value === '') return null
  return `${value}%`
}

const parseAprFormula = (formula) => {
  if (!formula) return null

  if (!formula.includes('IRR=')) {
    return { incomplete: true, message: formula }
  }

  const outflowMatch = formula.match(/IRR=\(\((.+?)\)=(-?\d+)\)/)
  const cashOutflows = []
  let netOutflow = null

  if (outflowMatch) {
    netOutflow = outflowMatch[2]
    const re = /([+-]?)\s*([^:+]+):([\d,.]+)/g
    let match
    while ((match = re.exec(outflowMatch[1])) !== null) {
      cashOutflows.push({
        label: formatCashFlowLabel(match[2].trim()),
        amount: match[3].trim(),
        sign: match[1] === '-' ? '−' : '+',
      })
    }
  }

  const emiMatches = [...formula.matchAll(/emi\s+(\d+):\s*(-?\d+(?:\.\d+)?)/gi)]
  const emiGroups = []

  emiMatches.forEach(([, monthStr, amount]) => {
    const month = parseInt(monthStr, 10)
    const last = emiGroups[emiGroups.length - 1]
    if (last && last.amount === amount && month === last.endMonth + 1) {
      last.endMonth = month
    } else {
      emiGroups.push({ startMonth: month, endMonth: month, amount })
    }
  })

  return {
    cashOutflows,
    netOutflow,
    emiGroups,
    totalMonths: emiMatches.length,
    raw: formula,
  }
}

const AprCalculationUI = ({ apr, aprFormula, termSheetApr }) => {
  const termSheetAprF = formatApr(termSheetApr)
  const calculatedAprF = formatApr(apr)

  if (!termSheetAprF && !calculatedAprF) return null

  const parsed = parseAprFormula(aprFormula)
  const differs = termSheetAprF && calculatedAprF && String(termSheetApr) !== String(apr)

  return (
    <CardUI className='ts_apr_card' title='APR'>
      <div className={`ts_apr_pair ${differs ? '' : 'ts_apr_pair_single'}`}>
        {
          differs &&
          <div className='ts_apr_pair_item'>
            <span className='ts_apr_pair_label'>Term sheet APR</span>
            <span className='ts_apr_value'>{termSheetAprF}</span>
            <span className='g_muted g_small'>On document</span>
          </div>
        }
        <div className='ts_apr_pair_item'>
          <span className='ts_apr_pair_label'>{differs ? 'Calculated APR' : 'APR'}</span>
          <span className='ts_apr_value'>{calculatedAprF || termSheetAprF}</span>
          <span className='g_muted g_small'>
            {
              calculatedAprF && parsed && !parsed.incomplete && parsed.totalMonths > 0
                ? `IRR × 12 · ${parsed.totalMonths} cash flows`
                : calculatedAprF ? 'Live from current terms' : 'On document'
            }
          </span>
        </div>
      </div>

      {
        differs &&
        <p className='ts_apr_diff'>
          Values differ — term sheet APR was overridden or is from an earlier terms snapshot.
        </p>
      }

      {
        parsed && parsed.incomplete &&
        <p className='g_muted g_m_top_50'>{parsed.message}</p>
      }

      {
        parsed && !parsed.incomplete &&
        <details className='ts_apr_breakdown g_m_top_100'>
          <summary>View calculation breakdown</summary>

          <div className='ts_apr_breakdown_body'>
            {
              parsed.cashOutflows.length > 0 &&
              <>
                <KvSectionUI label='Initial outflow (month 0)' />
                {
                  parsed.cashOutflows.map((item, i) =>
                    <KvRowUI
                      key={i}
                      label={item.label}
                      value={`${item.sign} ₹${item.amount}`}
                    />
                  )
                }
                {
                  parsed.netOutflow != null &&
                  <KvRowUI
                    label='Net outflow'
                    value={formatPaise(parsed.netOutflow)}
                    emphasis
                  />
                }
              </>
            }

            {
              parsed.emiGroups.length > 0 &&
              <>
                <KvSectionUI label='EMI schedule (inflows)' />
                <div className='ts_apr_emi_table_wrap'>
                  <table className='table_cr table ts_apr_emi_table'>
                    <thead>
                      <tr>
                        <th>Period</th>
                        <th>EMI amount</th>
                        <th className='ts_apr_col_count'># months</th>
                      </tr>
                    </thead>
                    <tbody>
                      {
                        parsed.emiGroups.map((group, i) =>
                          <tr key={i}>
                            <td>{formatMonthRange(group.startMonth, group.endMonth)}</td>
                            <td>{formatPaise(group.amount)}</td>
                            <td className='ts_apr_col_count'>
                              {group.endMonth - group.startMonth + 1}
                            </td>
                          </tr>
                        )
                      }
                    </tbody>
                    <tfoot>
                      <tr>
                        <td>Total</td>
                        <td />
                        <td className='ts_apr_col_count'>{parsed.totalMonths}</td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              </>
            }

            <details className='ts_apr_raw g_m_top_100'>
              <summary>Raw formula</summary>
              <pre className='g_muted'>{parsed.raw}</pre>
            </details>
          </div>
        </details>
      }
    </CardUI>
  )
}

export default AprCalculationUI
